// @tennis-matches/services/governance
// Migrated from src/services/governance/role-bindings.remote.js
// Best-effort Governance helpers (role_bindings) in app_config schema.
// Fallback: wenn schema-Zugriff auf app_config scheitert, RPC-basierter Ladeweg.

import { getSupabaseClient, getSupabaseUser } from "../supabase/index.js";
import { isAppConfigSchemaUnavailableError } from "../supabase/rest-endpoint-availability.js";
import { rejectInvalidTenantScopeId } from "./scopeIdValidation.js";

const SCHEMA = "app_config";
const T_BINDINGS = "role_bindings";

function __asStr(v) { return String(v ?? "").trim(); }
function __db(supabase) {
  return (supabase && typeof supabase.schema === "function") ? supabase.schema(SCHEMA) : supabase;
}

function __isMissingTableError(error) {
  const code = __asStr(error?.code);
  const status = Number(error?.status || 0);
  return (
    status === 404 ||
    code === "42P01" || // undefined_table
    code === "42703" || // undefined_column
    code === "PGRST116" // not found (schema/table not exposed)
  );
}

function __isSchemaAccessError(error) {
  return isAppConfigSchemaUnavailableError(error) || __isMissingTableError(error);
}

function __devLog(tag, extra = {}) {
  try {
    console.debug(`[role-bindings] ${tag}`, extra);
  } catch (_) {}
}

const MY_ROLE_BINDINGS_CACHE_MS = 30000;
/** @type {Promise<{ ok: boolean, bindings?: object[], reason?: string, missing?: boolean, error?: unknown }> | null} */
let __myRoleBindingsInflight = null;
let __myRoleBindingsInflightUid = "";
/** @type {{ uid: string, ts: number, result: object } | null} */
let __myRoleBindingsCache = null;

export function invalidateMyRoleBindingsCache() {
  __myRoleBindingsCache = null;
  __myRoleBindingsInflight = null;
  __myRoleBindingsInflightUid = "";
}

export async function getCurrentAuthUserIdRemote() {
  const u = await getSupabaseUser();
  const uid = __asStr(u?.id);
  return uid ? { ok: true, userId: uid } : { ok: false, reason: "no-user" };
}

/** Vollständiges `user`-Objekt (u. a. `app_metadata`) für Governance-Guards. */
export async function getCurrentAuthUserRemote() {
  const u = await getSupabaseUser();
  return u ? { ok: true, user: u } : { ok: false, reason: "no-user", user: null };
}

function __normalizeBindingsList(data) {
  const list = Array.isArray(data) ? data : [];
  return list.filter((b) => b?.is_active !== false);
}

async function __loadMyRoleBindingsViaRpc(supabase) {
  try {
    const { data, error } = await supabase.rpc("list_my_role_bindings");
    if (!error && Array.isArray(data)) {
      const bindings = __normalizeBindingsList(data);
      __devLog("RPC list_my_role_bindings succeeded", { count: bindings.length });
      return { ok: true, bindings };
    }
    if (error) {
      const code = __asStr(error?.code);
      const msg = __asStr(error?.message).toLowerCase();
      const isRpcMissing =
        code === "42883" ||
        code === "PGRST202" ||
        msg.includes("could not find the function") ||
        msg.includes("function not found");
      if (!isRpcMissing) {
        __devLog("RPC list_my_role_bindings failed", { code, message: error?.message });
      }
    }
  } catch (e) {
    __devLog("RPC list_my_role_bindings exception", { err: String(e?.message || e) });
  }
  return null;
}

export async function loadRoleBindingsRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const db = __db(supabase);

  const { data, error } = await db
    .from(T_BINDINGS)
    .select("id,scope,scope_id,user_id,role,is_active,created_at,updated_at")
    .order("scope", { ascending: true })
    .order("scope_id", { ascending: true })
    .order("role", { ascending: true })
    .order("created_at", { ascending: false });

  if (error) {
    if (__isMissingTableError(error)) {
      __devLog("schema-access failed, trying RPC fallback", {
        code: error?.code,
        status: error?.status,
        message: error?.message,
      });
      const rpcRes = await __loadMyRoleBindingsViaRpc(supabase);
      if (rpcRes) return rpcRes;
      return await __loadRoleBindingsFromPublicTable(supabase);
    }
    return { ok: false, reason: "db-error", error };
  }
  const bindings = __normalizeBindingsList(data);
  __devLog("loaded bindings via schema", { count: bindings.length });
  return { ok: true, bindings };
}

async function __loadRoleBindingsViaRpcFallback(supabase) {
  const rpcRes = await __loadMyRoleBindingsViaRpc(supabase);
  if (rpcRes) return rpcRes;
  return await __loadRoleBindingsFromPublicTable(supabase);
}

async function __loadRoleBindingsFromPublicTable(supabase) {
  try {
    const { data, error } = await supabase
      .from("role_bindings")
      .select("id,scope,scope_id,user_id,role,created_at,updated_at")
      .order("created_at", { ascending: false });
    if (!error && Array.isArray(data)) {
      __devLog("public.role_bindings fallback succeeded", { count: data.length });
      return { ok: true, bindings: data };
    }
    if (error && __isMissingTableError(error)) {
      __devLog("public.role_bindings also missing", { code: error?.code });
      return { ok: true, bindings: [], missing: true };
    }
    if (error) {
      __devLog("public.role_bindings query error", { code: error?.code, message: error?.message });
      return { ok: true, bindings: [], missing: true };
    }
  } catch (e) {
    __devLog("public.role_bindings fallback exception", { err: String(e?.message || e) });
  }
  return { ok: true, bindings: [], missing: true };
}

/**
 * Loads role bindings for the current authenticated user (RLS-enforced).
 * NOTE: The underlying query is identical to loadRoleBindingsRemote().
 * The distinction is semantic: callers use this for "my roles" only.
 */
export async function loadMyRoleBindingsRemote(options = {}) {
  const force = Boolean(options?.force);
  const u = await getSupabaseUser();
  const uid = __asStr(u?.id);
  const now = Date.now();

  if (
    !force &&
    uid &&
    __myRoleBindingsCache &&
    __myRoleBindingsCache.uid === uid &&
    now - __myRoleBindingsCache.ts < MY_ROLE_BINDINGS_CACHE_MS
  ) {
    return __myRoleBindingsCache.result;
  }

  if (!force && __myRoleBindingsInflight && __myRoleBindingsInflightUid === uid) {
    return __myRoleBindingsInflight;
  }

  const promise = __loadMyRoleBindingsRemoteImpl();
  __myRoleBindingsInflight = promise;
  __myRoleBindingsInflightUid = uid;
  try {
    const result = await promise;
    if (uid && result?.ok) {
      __myRoleBindingsCache = { uid, ts: Date.now(), result };
    }
    return result;
  } finally {
    if (__myRoleBindingsInflight === promise) {
      __myRoleBindingsInflight = null;
      __myRoleBindingsInflightUid = "";
    }
  }
}

async function __loadMyRoleBindingsRemoteImpl() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const rpcRes = await __loadMyRoleBindingsViaRpc(supabase);
  if (rpcRes) return rpcRes;

  return await loadRoleBindingsRemote();
}

async function __loadRoleBindingsForScopeViaRpc(supabase, scope, scopeId) {
  try {
    const { data, error } = await supabase.rpc("list_role_bindings_for_scope", {
      p_scope: scope,
      p_scope_id: scopeId,
    });
    if (!error && Array.isArray(data)) {
      __devLog("RPC list_role_bindings_for_scope succeeded", { count: data.length, scope, scopeId });
      return { ok: true, bindings: __normalizeBindingsList(data) };
    }
    if (error) {
      __devLog("RPC list_role_bindings_for_scope failed", { code: error?.code, message: error?.message });
      return { ok: false, reason: "rpc-error", error };
    }
  } catch (e) {
    __devLog("RPC list_role_bindings_for_scope exception", { err: String(e?.message || e) });
  }
  return null;
}

export async function loadRoleBindingsForScopeRemote(scope, scopeId) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  if (!s || !sid) return { ok: false, reason: "missing-scope" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const rpcFirst = await __loadRoleBindingsForScopeViaRpc(supabase, s, sid);
  if (rpcFirst) return rpcFirst;

  const db = __db(supabase);

  const { data, error } = await db
    .from(T_BINDINGS)
    .select("id,scope,scope_id,user_id,role,is_active,created_at,updated_at")
    .eq("scope", s)
    .eq("scope_id", sid)
    .order("role", { ascending: true })
    .order("created_at", { ascending: false })
    .limit(2000);

  if (error) {
    if (__isSchemaAccessError(error)) {
      const rpcRes = await __loadRoleBindingsForScopeViaRpc(supabase, s, sid);
      if (rpcRes) return rpcRes;
      return { ok: true, bindings: [], missing: true };
    }
    if (__isMissingTableError(error)) return { ok: true, bindings: [], missing: true };
    return { ok: false, reason: "db-error", error };
  }
  return { ok: true, bindings: Array.isArray(data) ? data : [] };
}

/**
 * user_ids globaler Superadmins (scope=global, role=superadmin).
 * Für UI-Filter: Superadmins nicht in Vereins-Mitgliederlisten anzeigen.
 *
 * @returns {Promise<{ok:boolean, userIds?:string[], reason?:string, error?:unknown}>}
 */
export async function loadGlobalSuperadminUserIdsRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", userIds: [] };

  let data = null;
  let error = null;
  ({ data, error } = await supabase.rpc("list_global_superadmin_user_ids"));

  if (error && __isSchemaAccessError(error)) {
    const db = __db(supabase);
    ({ data, error } = await db.rpc("list_global_superadmin_user_ids"));
  }

  if (error) {
    if (__isMissingTableError(error)) return { ok: true, userIds: [], missing: true };
    const authReason = __rpcAuthReason(error);
    return { ok: false, reason: authReason || "rpc-error", userIds: [], error };
  }

  const userIds = Array.isArray(data)
    ? [...new Set(data.map((id) => __asStr(id)).filter(Boolean))]
    : [];
  return { ok: true, userIds };
}

function __rpcAuthReason(error) {
  const msg = __asStr(error?.message);
  if (!msg) return null;
  if (msg.includes("not-authorized")) return "not-authorized";
  if (msg.includes("not-authenticated")) return "not-authenticated";
  return null;
}

/**
 * @deprecated Direkte Rolle-Bindings dürfen nicht mehr client-seitig geschrieben werden.
 * Diese Funktion nutzt deshalb die SECURITY DEFINER RPC `app_config.add_role_binding`
 * (nur global superadmin). Für normale Flows bitte `access_requests` verwenden.
 */
export async function addRoleBindingRemote({ scope, scopeId, userId, role } = {}) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  const uid = __asStr(userId);
  const r = __asStr(role);

  if (!s || !sid || !uid || !r) return { ok: false, reason: "missing-fields" };
  const scopeErr = rejectInvalidTenantScopeId(s, sid);
  if (scopeErr) return scopeErr;

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const db = __db(supabase);

  // DEPRECATED (security): do NOT write role_bindings directly from client.
  // Use SECURITY DEFINER RPC (superadmin only) to keep writes controlled/auditable.
  const { data, error } = await db.rpc("add_role_binding", {
    p_scope: s,
    p_scope_id: sid,
    p_user_id: uid,
    p_role: r,
  });

  if (error) {
    if (__isMissingTableError(error)) return { ok: false, reason: "table-missing", error };
    const authReason = __rpcAuthReason(error);
    return { ok: false, reason: authReason || "rpc-error", error };
  }

  return { ok: true, binding: data || null, deprecated: true };
}

/**
 * @deprecated Direkte Rolle-Bindings dürfen nicht mehr client-seitig gelöscht werden.
 * Diese Funktion nutzt deshalb die SECURITY DEFINER RPC `app_config.delete_role_binding`
 * (nur global superadmin). Für normale Flows bitte `access_requests` verwenden.
 */
export async function deleteRoleBindingRemote(id) {
  const rid = __asStr(id);
  if (!rid) return { ok: false, reason: "missing-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const db = __db(supabase);

  // DEPRECATED (security): do NOT delete role_bindings directly from client.
  // Use SECURITY DEFINER RPC (superadmin only).
  const { data, error } = await db.rpc("delete_role_binding", { p_id: rid });
  if (error) {
    const authReason = __rpcAuthReason(error);
    return { ok: false, reason: authReason || "rpc-error", error };
  }
  return { ok: true, result: data || null, deprecated: true };
}
